<template>
    <gmap-map
        ref="map"
        :center="center"
        :zoom="zoomValue"
        @bounds_changed="boundsHasChanged"
    >
        <delivery-projects-renderer
            v-if="mapLoaded"
            :drawing-manager="drawingManager"
            :can-edit="canEdit"
            :delivery-projects="paths"
            :current-delivery-project="currentDeliveryProject"
            @rendered="$emit('rendered')"
        />
        <gmap-marker
            v-for="(address, index) in addresses"
            :key="index"
            :icon="localMarkerIcon(address)"
            :position="markerPosition(address)"
            :clickable="false"
            :draggable="false"
        />
        <slot />
        <div
            v-if="loading"
            slot="visible"
        >
            <div class="bg-white text-black text-xs rounded p-sm absolute z-50 pin-l pin-b mb-sm ml-sm">
                <font-awesome-icon icon="spinner" spin />
                <span class="ml-xs">Laddar adresser...</span>
            </div>
        </div>
        <div
            v-else-if="wontShowAddresses"
            slot="visible"
        >
            <div class="bg-white text-black text-xs rounded p-sm absolute z-50 pin-l pin-b mb-sm ml-sm">
                <span class="ml-xs">Zooma in för att ladda adresser</span>
            </div>
        </div>
    </gmap-map>
</template>

<script>
    import debounce from 'lodash-es/debounce';
    import Drawing from '../Maps/Drawing';
    import Coordinates from '../Maps/Coordinates';
    import DeliveryProjectsRenderer from './DeliveryProjectsRenderer';
    import LocalMarkerIcon from '../../../shared/components/Maps/LocalMarkerIcon.vue';
    import AddressesInViewport from '../Maps/AddressesInViewport';

    export default {
        components: {
            DeliveryProjectsRenderer,
        },
        extends: AddressesInViewport,
        mixins: [LocalMarkerIcon],
        props: {
            deliveryProjects: {
                type: Array,
                default: null,
            },
            currentDeliveryProject: {
                type: Object,
                required: true,
            },
            center: {
                type: Object,
                default: () => {
                    return window.defaultMapCenter;
                },
            },
            canDraw: {
                required: false,
                type: Boolean,
                default: false,
            },
            canEdit: {
                required: false,
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                initialLoad: true,
                mapLoaded: false,
                drawingInitialized: false,
                drawingManager: null,
                addresses: [],
            };
        },
        computed: {
            zoomValue() {
                return parseInt(8);
            },
            paths() {
                return this.deliveryProjects.map((deliveryProject) => {
                    return {
                        path: deliveryProject.polygon_path,
                        id: deliveryProject.id,
                    };
                });
            },
        },
        watch: {
            mapLoaded(isLoaded) {
                if (! isLoaded) {
                    return;
                }

                this.$emit('rendered', true);
            },
        },
        created() {
            this.$nextTick(() => {
                this.$refs.map.$mapPromise.then(() => {
                    this.drawingManager = new Drawing(this.$refs.map.$mapObject);
                    this.mapLoaded = true;
                    this.drawShapes();
                });
            });

            EventBus.$on(
                'zoom-to-shape',
                shapeId => this.zoomToShape(shapeId),
            );

            EventBus.$on(
                'clear-last-shape',
                () => {
                    this.drawingManager.resetLastShape();
                },
            );

            EventBus.$on(
                'clear-user-created-shapes',
                (drawnShapes) => {
                    this.drawingManager.resetShapes(drawnShapes);
                },
            );

            EventBus.$on(
                'delivery-projects-shapes-drawn',
                () => {
                    if (this.drawingManager.shapes.length > 0) {
                        this.drawingManager.fitToShapes(this.drawingManager.shapes);
                    }
                },
            );
        },
        methods: {
            markerPosition(marker) {
                return {
                    lat: parseFloat(marker.lat),
                    lng: parseFloat(marker.lng),
                };
            },
            zoomToShape(shapeId) {
                this.drawingManager.fitToShape(shapeId);
            },
            drawShapes() {
                if (! this.mapLoaded) {
                    return;
                }

                if (! this.coordinates) {
                    this.coordinates = new Coordinates();
                }

                if (this.canDraw && ! this.drawingInitialized) {
                    this.drawingInitialized = true;
                    this.drawingManager.initializeDrawing();
                    this.drawingManager.finishedDrawing((shape) => {
                        const coordinates = this.coordinates.getCoordinatesFromShape(shape);
                        const polygon = this.coordinates.getStringifiedCoordinates(coordinates);
                        EventBus.$emit('shape-drawn', polygon, shape);
                    });
                }
            },
            boundsHasChanged() {
                this.$emit('boundsChanged', this.$refs.map);
                this.fetchAddressesWithinViewport(this.$refs.map);
            },
            /** Overrides method in AddressesInViewport */
            fetchAddresses: debounce(async function (coordinates) {
                const result = await axios.post(route('api.addresses.withinBounds'), {
                    coordinates,
                    delivery_project_id: this.currentDeliveryProject.id,
                });

                this.addresses = result.data;
                this.loading = false;
            }, 2000),
        },
    };
</script>
